import { useMemo } from 'react';

/**
 * /auth/choose — workspace picker.
 *
 * Entry: platform.khal.ai/v1/auth/callback redirects here with
 *   #token=<jwt>&hint=<kernel-url>
 *
 * The JWT's `kernels` claim lists every workspace the platform considers
 * reachable. This screen decodes the claim client-side (no verification —
 * the kernel re-verifies the signature at /api/auth/accept) and renders a
 * tile per workspace. Click a tile → redirect to /auth/accept#kernel=X&token=Y,
 * which is the existing server-verified handoff.
 *
 * The picker always renders when reached (no fast-path skip even for
 * single-kernel claims) — choosing a workspace IS the primary sign-in
 * gesture and users have explicitly asked to confirm which instance
 * they're entering before landing on a desktop.
 *
 * No silent fallbacks: a malformed token, zero kernels, or decode error
 * renders a loud error, never a stale desktop.
 *
 * v2: converged to canonical --khal-* token grammar. No raw hex, no
 *     blue/purple gradient tile. Workspace picker now uses KHAL dark
 *     card grammar via auth-overlay / auth-card / --khal-* tokens.
 */

/**
 * `kernels` may arrive as either:
 *  - the legacy `string[]` of kernel URLs (pre-enterprise-customer-onboarding),
 *  - or the enriched `EnrichedKernel[]` shape Group 6 will populate once the
 *    new Customer registry is live: each entry carries `customerId`,
 *    `customerName`, and `env` so the picker can group by Customer and label
 *    rows by environment (production / staging / sandbox / per-region).
 *
 * The component renders both shapes — there is no breaking change for users
 * holding tokens issued before Group 6 lands.
 */
interface EnrichedKernel {
	url: string;
	customerId?: string;
	customerName?: string;
	env?: string;
}

type KernelClaim = string | EnrichedKernel;

interface JwtClaims {
	sub?: string;
	email?: string;
	kernels?: KernelClaim[];
	iss?: string;
	exp?: number;
}

function normalizeKernel(claim: KernelClaim): EnrichedKernel {
	return typeof claim === 'string' ? { url: claim } : claim;
}

function groupKernels(kernels: EnrichedKernel[]): Array<{ key: string; label: string; entries: EnrichedKernel[] }> {
	const groups = new Map<string, { label: string; entries: EnrichedKernel[] }>();
	for (const k of kernels) {
		const key = k.customerId ?? k.url;
		const label = k.customerName ?? kernelLabel(k.url);
		const existing = groups.get(key);
		if (existing) {
			existing.entries.push(k);
		} else {
			groups.set(key, { label, entries: [k] });
		}
	}
	return Array.from(groups.entries()).map(([key, value]) => ({ key, ...value }));
}

function parseFragment(hash: string): { token: string | null; hint: string | null } {
	const h = hash.startsWith('#') ? hash.slice(1) : hash;
	const params = new URLSearchParams(h);
	return { token: params.get('token'), hint: params.get('hint') };
}

function decodeJwtPayload(token: string): JwtClaims | null {
	try {
		const parts = token.split('.');
		if (parts.length !== 3) return null;
		const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
		return JSON.parse(atob(padded)) as JwtClaims;
	} catch {
		return null;
	}
}

function kernelLabel(kernelUrl: string): string {
	try {
		const { hostname } = new URL(kernelUrl);
		return hostname.split('.')[0] || hostname;
	} catch {
		return kernelUrl;
	}
}

function kernelHost(kernelUrl: string): string {
	try {
		return new URL(kernelUrl).hostname;
	} catch {
		return kernelUrl;
	}
}

function handoffToAccept(kernelUrl: string, token: string): void {
	window.location.replace(
		`/auth/accept#kernel=${encodeURIComponent(kernelUrl)}&token=${encodeURIComponent(token)}`,
	);
}

/* ── Inline style helpers — all resolve through --khal-* tokens ────────── */

const chooseOverlay: React.CSSProperties = {
	position: 'fixed',
	inset: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	background: 'var(--khal-bg)',
	color: 'var(--khal-fg)',
	fontFamily: 'var(--khal-font-body)',
	padding: '2rem',
};

const chooseCard: React.CSSProperties = {
	width: '100%',
	maxWidth: 440,
	display: 'flex',
	flexDirection: 'column',
	gap: '1.25rem',
	background: 'var(--khal-surface)',
	border: '1px solid var(--khal-border)',
	borderRadius: 'var(--khal-radius-lg)',
	boxShadow: 'var(--khal-shadow-lg)',
	overflow: 'hidden',
};

const chooseHead: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	gap: 10,
	padding: '18px 20px 0',
	color: 'var(--khal-fg)',
};

const chooseEye: React.CSSProperties = {
	fontFamily: 'var(--khal-font-mono)',
	fontSize: '10.5px',
	color: 'var(--khal-tertiary)',
	textTransform: 'lowercase',
	letterSpacing: '0.05em',
};

const chooseBody: React.CSSProperties = {
	padding: '4px 20px 4px',
	display: 'flex',
	flexDirection: 'column',
	gap: 10,
};

const chooseFoot: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: '8px 20px 16px',
	borderTop: '1px solid var(--khal-border)',
	marginTop: 4,
};

const chooseMeta: React.CSSProperties = {
	fontFamily: 'var(--khal-font-mono)',
	fontSize: '10.5px',
	color: 'var(--khal-tertiary)',
};

/* ── Workspace list styles ─────────────────────────────────────────────── */

const wsSection: React.CSSProperties = {
	display: 'grid',
	gap: '0.5rem',
};

const wsGroupLabel: React.CSSProperties = {
	fontSize: '0.7rem',
	fontWeight: 600,
	color: 'var(--khal-tertiary)',
	textTransform: 'uppercase',
	letterSpacing: '0.08em',
	margin: '0 0 0.25rem 0',
	fontFamily: 'var(--khal-font-mono)',
};

const wsTile: React.CSSProperties = {
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	gap: '1rem',
	padding: '1rem 1.25rem',
	background: 'var(--khal-cell)',
	border: '1px solid var(--khal-border)',
	borderRadius: 'var(--khal-radius)',
	cursor: 'pointer',
	textAlign: 'left',
	color: 'var(--khal-fg)',
	fontFamily: 'var(--khal-font-body)',
	fontSize: '0.95rem',
	transition: 'background var(--khal-duration) var(--khal-ease), border-color var(--khal-duration) var(--khal-ease)',
};

const wsTileHover: React.CSSProperties = {
	background: 'color-mix(in oklch, var(--khal-accent) 12%, var(--khal-cell))',
	borderColor: 'var(--khal-border-strong)',
};

const wsIconCell: React.CSSProperties = {
	width: 44,
	height: 44,
	flexShrink: 0,
	borderRadius: 'var(--khal-radius-sm)',
	background: 'var(--khal-chrome)',
	border: '1px solid var(--khal-border)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	fontSize: '1.1rem',
	fontWeight: 600,
	color: 'var(--khal-accent)',
	fontFamily: 'var(--khal-font-display)',
};

const wsName: React.CSSProperties = {
	fontWeight: 600,
	marginBottom: 2,
	color: 'var(--khal-fg)',
};

const wsHost: React.CSSProperties = {
	fontFamily: 'var(--khal-font-mono)',
	fontSize: '0.8rem',
	color: 'var(--khal-tertiary)',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const wsChevron: React.CSSProperties = {
	color: 'var(--khal-tertiary)',
	fontSize: '1.2rem',
	fontFamily: 'var(--khal-font-mono)',
};

/* ── Header / email ────────────────────────────────────────────────────── */

const headerWrapper: React.CSSProperties = {
	width: '100%',
	maxWidth: 520,
};

const headerStyle: React.CSSProperties = {
	marginBottom: '2rem',
	textAlign: 'center',
};

const headingH1: React.CSSProperties = {
	fontSize: '1.5rem',
	margin: '0 0 0.5rem 0',
	color: 'var(--khal-fg)',
	fontFamily: 'var(--khal-font-display)',
	fontWeight: 650,
};

const emailText: React.CSSProperties = {
	margin: 0,
	color: 'var(--khal-muted)',
	fontSize: '0.9rem',
};

const errorLink: React.CSSProperties = {
	color: 'var(--khal-accent-blue)',
	fontFamily: 'var(--khal-font-mono)',
};

const errorBox: React.CSSProperties = {
	textAlign: 'center',
	maxWidth: 440,
};

const errorH1: React.CSSProperties = {
	fontSize: '1.3rem',
	margin: '0 0 0.75rem 0',
	color: 'var(--khal-fg)',
	fontFamily: 'var(--khal-font-display)',
	fontWeight: 650,
};

const errorDetail: React.CSSProperties = {
	fontFamily: 'var(--khal-font-body)',
	fontSize: '0.9rem',
	color: 'var(--khal-muted)',
	margin: 0,
	lineHeight: 1.5,
};

/* ── Component ──────────────────────────────────────────────────────────── */

function ChooseShell({ children }: { children: React.ReactNode }) {
	return <div style={chooseOverlay}>{children}</div>;
}

function ErrorBlock({ title, detail }: { title: string; detail: string }) {
	return (
		<div style={errorBox}>
			<h1 style={errorH1}>{title}</h1>
			<p style={errorDetail}>{detail}</p>
			<p style={{ marginTop: '1.5rem' }}>
				<a href="/" style={errorLink}>
					Back to sign-in
				</a>
			</p>
		</div>
	);
}

export function AuthChooseScreen() {
	const { token } = useMemo(() => parseFragment(window.location.hash), []);
	const claims = useMemo(() => (token ? decodeJwtPayload(token) : null), [token]);
	const rawKernels = claims?.kernels ?? [];
	const kernels = useMemo(() => rawKernels.map(normalizeKernel), [rawKernels]);
	const groups = useMemo(() => groupKernels(kernels), [kernels]);

	// Loud failure for malformed / empty-claim tokens — never fall through.
	if (!token) {
		return (
			<ChooseShell>
				<ErrorBlock
					title="Sign-in link is missing a token"
					detail="Return to the sign-in page and try again."
				/>
			</ChooseShell>
		);
	}
	if (!claims || kernels.length === 0) {
		return (
			<ChooseShell>
				<ErrorBlock
					title="No workspaces available"
					detail={
						claims?.email
							? `${claims.email} is not assigned to any Khal workspace. Contact your administrator.`
							: 'Your sign-in link did not carry any workspace assignments.'
					}
				/>
			</ChooseShell>
		);
	}

	const showGrouping = groups.some((g) => g.entries.length > 1 || g.entries.some((e) => e.env));

	return (
		<ChooseShell>
			<div style={headerWrapper}>
				<header style={headerStyle}>
					<h1 style={headingH1}>Choose a workspace</h1>
					{claims.email && <p style={emailText}>Signed in as {claims.email}</p>}
				</header>

				<div style={{ display: 'grid', gap: '1.25rem' }}>
					{groups.map((group) => (
						<section key={group.key} style={wsSection}>
							{showGrouping && <h2 style={wsGroupLabel}>{group.label}</h2>}
							<ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' }}>
								{group.entries.map((kernel) => (
									<li key={kernel.url}>
										<button
											type="button"
											onClick={() => handoffToAccept(kernel.url, token)}
											style={wsTile}
											onMouseEnter={(e) => {
												e.currentTarget.style.background = 'var(--khal-surface)';
												e.currentTarget.style.borderColor = 'var(--khal-border-strong)';
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.background = 'var(--khal-cell)';
												e.currentTarget.style.borderColor = 'var(--khal-border)';
											}}
										>
											<div style={wsIconCell}>
												{(kernel.customerName ?? kernelLabel(kernel.url)).slice(0, 2).toUpperCase()}
											</div>
											<div style={{ flex: 1, minWidth: 0 }}>
												<div style={wsName}>
													{kernel.env ?? kernelLabel(kernel.url)}
												</div>
												<div style={wsHost}>{kernelHost(kernel.url)}</div>
											</div>
											<div style={wsChevron}>›</div>
										</button>
									</li>
								))}
							</ul>
						</section>
					))}
				</div>
			</div>
		</ChooseShell>
	);
}
